// Loan Compliance Module v1.0 PR 7 + PR 9 — raw HTTP transport for the
// Track 3 Annual Legal Compliance Review (loan and pool scopes). Hooks
// in @/hooks/compliance/useLegalReview wrap these with TanStack Query.

import { api } from "../client";
import type {
  CreateLegalReviewMemoRequest,
  CreateLoanLegalReviewRequest,
  CreatePoolLegalReviewMemoRequest,
  CreatePoolLegalReviewRequest,
  FinalizeLoanLegalReviewRequest,
  FinalizePoolLegalReviewRequest,
  LegalReviewMemoDto,
  LoanLegalReviewDto,
  LoanLegalReviewSummaryDto,
  PoolLegalReviewDto,
  PoolLegalReviewMemoDto,
  PoolLegalReviewSummaryDto,
  PoolSupervisorAcceptanceRequest,
  RecordModuleConclusionRequest,
  RecordPoolModuleConclusionRequest,
  ReissueLoanLegalReviewRequest,
  ReissuePoolLegalReviewRequest,
  SignLegalReviewMemoRequest,
  SignPoolLegalReviewMemoRequest,
  SupervisorAcceptanceRequest,
  UpdatePoolScopingMemoRequest,
  UpdateScopingMemoRequest,
} from "./legalReviewTypes";

type AnyLoanReview = LoanLegalReviewDto | LoanLegalReviewSummaryDto;
type AnyPoolReview = PoolLegalReviewDto | PoolLegalReviewSummaryDto;

const loanBase = (loanId: string) =>
  `/compliance/track3/legal/loans/${loanId}`;

const poolBase = (poolId: string) =>
  `/compliance/track3/legal/pools/${poolId}`;

export const loanLegalReviewsApi = {
  list: (loanId: string) =>
    api.get<AnyLoanReview[]>(`${loanBase(loanId)}/reviews`),
  get: (loanId: string, reviewId: string) =>
    api.get<AnyLoanReview>(`${loanBase(loanId)}/reviews/${reviewId}`),
  create: (loanId: string, body: CreateLoanLegalReviewRequest) =>
    api.post<LoanLegalReviewDto>(`${loanBase(loanId)}/reviews`, body),
  updateScopingMemo: (
    loanId: string,
    reviewId: string,
    body: UpdateScopingMemoRequest,
  ) =>
    api.put<LoanLegalReviewDto>(
      `${loanBase(loanId)}/reviews/${reviewId}/scoping-memo`,
      body,
    ),
  recordModuleConclusion: (
    loanId: string,
    reviewId: string,
    body: RecordModuleConclusionRequest,
  ) =>
    api.post<LoanLegalReviewDto>(
      `${loanBase(loanId)}/reviews/${reviewId}/modules/conclusions`,
      body,
    ),
  generateDraft: (loanId: string, reviewId: string) =>
    api.post<LoanLegalReviewDto>(`${loanBase(loanId)}/reviews/${reviewId}/draft`),
  submitForReview: (loanId: string, reviewId: string) =>
    api.post<LoanLegalReviewDto>(
      `${loanBase(loanId)}/reviews/${reviewId}/submit`,
    ),
  recordSupervisorAcceptance: (
    loanId: string,
    reviewId: string,
    body: SupervisorAcceptanceRequest,
  ) =>
    api.post<LoanLegalReviewDto>(
      `${loanBase(loanId)}/reviews/${reviewId}/supervisor-acceptance`,
      body,
    ),
  finalize: (
    loanId: string,
    reviewId: string,
    body: FinalizeLoanLegalReviewRequest,
  ) =>
    api.post<LoanLegalReviewDto>(
      `${loanBase(loanId)}/reviews/${reviewId}/finalize`,
      body,
    ),
  reissue: (
    loanId: string,
    reviewId: string,
    body: ReissueLoanLegalReviewRequest,
  ) =>
    api.post<LoanLegalReviewDto>(
      `${loanBase(loanId)}/reviews/${reviewId}/reissue`,
      body,
    ),
};

export const legalReviewMemosApi = {
  list: (loanId: string) =>
    api.get<LegalReviewMemoDto[]>(`${loanBase(loanId)}/memos`),
  create: (loanId: string, body: CreateLegalReviewMemoRequest) =>
    api.post<LegalReviewMemoDto>(`${loanBase(loanId)}/memos`, body),
  sign: (
    loanId: string,
    memoId: string,
    body: SignLegalReviewMemoRequest,
  ) =>
    api.post<LegalReviewMemoDto>(
      `${loanBase(loanId)}/memos/${memoId}/sign`,
      body,
    ),
};

// =============================== Pool scope ==============================

export const poolLegalReviewsApi = {
  list: (poolId: string) =>
    api.get<AnyPoolReview[]>(`${poolBase(poolId)}/reviews`),
  get: (poolId: string, reviewId: string) =>
    api.get<AnyPoolReview>(`${poolBase(poolId)}/reviews/${reviewId}`),
  create: (poolId: string, body: CreatePoolLegalReviewRequest) =>
    api.post<PoolLegalReviewDto>(`${poolBase(poolId)}/reviews`, body),
  updateScopingMemo: (
    poolId: string,
    reviewId: string,
    body: UpdatePoolScopingMemoRequest,
  ) =>
    api.put<PoolLegalReviewDto>(
      `${poolBase(poolId)}/reviews/${reviewId}/scoping-memo`,
      body,
    ),
  recordModuleConclusion: (
    poolId: string,
    reviewId: string,
    body: RecordPoolModuleConclusionRequest,
  ) =>
    api.post<PoolLegalReviewDto>(
      `${poolBase(poolId)}/reviews/${reviewId}/modules/conclusions`,
      body,
    ),
  generateDraft: (poolId: string, reviewId: string) =>
    api.post<PoolLegalReviewDto>(`${poolBase(poolId)}/reviews/${reviewId}/draft`),
  generateOfficerCertSupport: (poolId: string, reviewId: string) =>
    api.post<PoolLegalReviewDto>(
      `${poolBase(poolId)}/reviews/${reviewId}/officer-cert-support`,
    ),
  submitForReview: (poolId: string, reviewId: string) =>
    api.post<PoolLegalReviewDto>(
      `${poolBase(poolId)}/reviews/${reviewId}/submit`,
    ),
  recordSupervisorAcceptance: (
    poolId: string,
    reviewId: string,
    body: PoolSupervisorAcceptanceRequest,
  ) =>
    api.post<PoolLegalReviewDto>(
      `${poolBase(poolId)}/reviews/${reviewId}/supervisor-acceptance`,
      body,
    ),
  finalize: (
    poolId: string,
    reviewId: string,
    body: FinalizePoolLegalReviewRequest,
  ) =>
    api.post<PoolLegalReviewDto>(
      `${poolBase(poolId)}/reviews/${reviewId}/finalize`,
      body,
    ),
  reissue: (
    poolId: string,
    reviewId: string,
    body: ReissuePoolLegalReviewRequest,
  ) =>
    api.post<PoolLegalReviewDto>(
      `${poolBase(poolId)}/reviews/${reviewId}/reissue`,
      body,
    ),
};

export const poolLegalReviewMemosApi = {
  list: (poolId: string) =>
    api.get<PoolLegalReviewMemoDto[]>(`${poolBase(poolId)}/memos`),
  create: (poolId: string, body: CreatePoolLegalReviewMemoRequest) =>
    api.post<PoolLegalReviewMemoDto>(`${poolBase(poolId)}/memos`, body),
  sign: (
    poolId: string,
    memoId: string,
    body: SignPoolLegalReviewMemoRequest,
  ) =>
    api.post<PoolLegalReviewMemoDto>(
      `${poolBase(poolId)}/memos/${memoId}/sign`,
      body,
    ),
};
